import React from 'react';

import PlanningCycleWorkbench from './PlanningCycleWorkbench';
import PlanningSetupWorkbench from './PlanningSetupWorkbench';
import { IPlanningControlCenterProps } from './PlanningControlCenter.types';
import { setBiePlanningCycle } from '../bieprofile/bieprofile.apis';

type PlanningControlMode = 'cycles' | 'workspace';

type State = {
  mode: PlanningControlMode;
  selectedCycleId: number | null;
};

/**
 * Navigation parent for the two independent Planning workbenches.
 *
 * 1. PlanningCycleWorkbench owns cycle portfolio/lifecycle operations.
 * 2. PlanningSetupWorkbench owns defaults, recommendations and readiness.
 */
export default class PlanningControlCenter extends React.Component<
  IPlanningControlCenterProps,
  State
> {
  private cycleWorkbenchRef =
    React.createRef<PlanningCycleWorkbench>();

  private setupWorkbenchRef =
    React.createRef<PlanningSetupWorkbench>();

  constructor(props: IPlanningControlCenterProps) {
    super(props);

    this.state = {
      mode: 'cycles',
      selectedCycleId: null,
    };
  }

  public refresh = (): void => {
    if (this.state.mode === 'cycles') {
      this.cycleWorkbenchRef.current?.refresh();
      return;
    }

    this.setupWorkbenchRef.current?.refresh();
  };

  private notify = (
    message: string,
    severity: 'success' | 'info' | 'warning' | 'error' = 'info',
  ): void => {
    this.props.showNotification?.(message, severity);
  };

  private handleOpenWorkspace = (
    planningCycleId: number,
  ): void => {
    this.props.onParam?.('set')?.(
      'planningcycleid',
      planningCycleId,
    );

    this.setState({
      mode: 'workspace',
      selectedCycleId: planningCycleId,
    });
  };

  private handleBackToCycles = (): void => {
    this.setState({
      mode: 'cycles',
      selectedCycleId: null,
    });
  };

  private handleSetCurrentContext0 = async (
    planningCycleId: number,
  ): Promise<void> => {
    try {
      await setBiePlanningCycle(planningCycleId);
      await this.props.refreshPlanningContext?.();

      this.notify(
        'Planning Cycle is now your current working context.',
        'success',
      );
    } catch (error) {
      this.notify(
        error instanceof Error
          ? error.message
          : 'Unable to set the Planning Cycle context.',
        'error',
      );
    }
  };

  protected handleSetCurrentContext = async (
      planningCycleId: number,
    ): Promise<void> => {
      try {
        const context = await setBiePlanningCycle(planningCycleId);
        this.props.setPlanningContext?.(context);
        this.props.showNotification?.(
          'Planning Cycle is now your current working context.',
          'success',
        );
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : 'Unable to update Planning Context.';
        this.props.showNotification?.(message, 'error');
        throw error;
      }
    };

  private renderCycleWorkbench(): React.ReactNode {
    return (
      <PlanningCycleWorkbench
        ref={this.cycleWorkbenchRef}
        {...this.props}
        onOpenWorkspace={this.handleOpenWorkspace}
        onSetCurrentContext={this.handleSetCurrentContext}
      />
    );
  }

  private renderSetupWorkbench(): React.ReactNode {
    const planningCycleId = this.state.selectedCycleId;

    if (planningCycleId == null) {
      return this.renderCycleWorkbench();
    }

    return (
      <PlanningSetupWorkbench
        ref={this.setupWorkbenchRef}
        {...this.props}
        planningCycleId={planningCycleId}
        onBack={this.handleBackToCycles}
      />
    );
  }

  render(): React.ReactNode {
    return this.state.mode === 'workspace'
      ? this.renderSetupWorkbench()
      : this.renderCycleWorkbench();
  }
}
